import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  C4ModelEquipesService,
  Equipes,
} from '../../../shared/services/api/c4-model/EquipesService';
import { useDebounce } from '../../../shared/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '../../../shared/layouts';
import { FerramentasDaListagem } from '../../../shared/components';
import { ApiException } from '../../../shared/services/api/ApiException';
import {
  confirm,
  feedback,
  traducaoComponenteDataGrid,
} from '../../../shared/services';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridSortModel,
} from '@mui/x-data-grid';
import { Box, Icon, IconButton } from '@mui/material';

export const ListagemEquipes: React.FC = () => {
  const { page = '1' } = useParams();

  const [rows, setRows] = useState<Equipes[]>([]);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [rowsCounter, setRowsCounter] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [offSet, setOffset] = useState(0);
  const [order, setOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('nome');
  const [limite, setLimite] = useState<number>(25);
  const [pesquisa, setPesquisa] = useState('');

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    sortModel.forEach((e) => {
      setSortBy(e.field);
      setOrder(e.sort || '');
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      C4ModelEquipesService.getAllEquipes(
        page,
        limite,
        offSet,
        pesquisa,
        order,
        sortBy
      ).then((result) => {
        setRows(result.data);
        setRowsCounter(result.totalCount);
      })
        .catch(error => {
          if (error instanceof ApiException) {
            feedback(error.message, 'error');
            setRows([]);
            setRowsCounter(0);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  }, [debounce, limite, offSet, order, sortBy, pesquisa]);

  const handleDelete = useCallback(
    (id: string, nome: string) => {
      const handleConfirmDelete = () => {
        feedback('Excluindo...!', 'loading');
        C4ModelEquipesService.deleteEquipeById(id)
          .then((result) => {
            setRows((oldRows) => oldRows?.filter((row) => row.id !== id));
            feedback(`${nome} Excluído com sucesso!`, 'success');
          })
          .catch(error => {
            if (error instanceof ApiException) {
              feedback(error.message, 'error');
            }
          });
      };
      confirm(
        `Tem certeza que deseja excluir ${nome}?`,
        'delete',
        handleConfirmDelete
      );
    },
    [setRows]
  );

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'actions',
        headerName: 'Ações',
        flex: 0.1,
        minWidth: 100,
        renderCell: (params: GridCellParams) => (
          <Box>
            <IconButton
              aria-label="delete"
              size="small"
              // disabled={permitirExcluirProduto}
              onClick={() => handleDelete(params.row.id, params.row.nome)}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
            <IconButton
              aria-label="edit"
              size="small"
              // disabled={permitirAtualizarProduto}
              onClick={() => navigate(`/c4-equipes/detalhe/${params.row.id}`)}
            >
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Box>
        ),
      },
      {
        field: 'nome',
        headerName: 'Nome',
        flex: 0.9,
      },
    ];
  }, [navigate, handleDelete]);

  return (
    <PageLayout
      alignTools="end"
      title="Equipes"
      tools={
        <FerramentasDaListagem
          //  esconderBotaoDeNovo={permitirIncluirLicenca}
          esconderCampoDeBusca={false}
          valorDaBusca={pesquisa}
          textoBotaoDeNovo="Nova"
          aoClicarEmNovo={() => navigate('/c4-equipes/detalhe/nova')}
          aoMudarBusca={(e) => setPesquisa(e)}
        />
      }
    >
      <Box
        sx={{ height: '100%', width: '100%' }}
        data-testid="tabela-produtos"
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <DataGrid
              localeText={traducaoComponenteDataGrid}
              rows={rows}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'nome', sort: 'asc' }],
                },
              }}
              columns={columns}
              pageSize={limite}
              disableSelectionOnClick
              rowsPerPageOptions={[20, 50, 100]}
              onPageSizeChange={(e) => {
                setLimite(e);
                setPagina(0);
              }}
              onPageChange={(newPage) => {
                setPagina(+newPage);
                setOffset(+newPage * limite);
                // navigate(`/c4-equipes/${(newPage + 1).toString()}/${busca}`);
              }}
              getRowId={(row) => row.id}
              loading={isLoading}
              rowCount={Math.ceil(rowsCounter)}
              pagination
              paginationMode="server"
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              autoHeight
              density="compact"
            />
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
};
